import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {PersistedRunQueryData} from 'analysis-tool/data-frame-creation/CreateDataFrameFromAnalyticsBlockViewSkeleton';
import {DataReference} from 'analysis-tool/ui/main-view/MainViewTypes';
import {BulkArchiveItemsResponse} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {Chart} from 'charts/ChartTypes';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameFilterStepConfig} from 'data-frames/filters/DataFrameFilterTypes';
import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {GetPipelineStepShape, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {OutputStepDatasRecord} from 'pipelines/StepDataUtils';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {DeepPartial, JSONObject, JSONValue, MakeFieldsOptional} from 'util/Types';

interface SendToDocumentStepInput {
  inputSavedResultsDataFrameIds: Array<string>;
  inputDatasetDataFrameIds: Array<string>;
  inputChartIds: Array<string>;
}

// TODO(BNCH-99103): Generate the shape of `stepInput` based on the step shape, similar to `stepInputParents`
type StepInputDataFrames<StepType extends PipelineStepType> =
  | {
      stepType: Exclude<StepType, PipelineStepType.JOIN | PipelineStepType.REGRESSION_INTERPOLATION>;
      stepInput?: {inputDataFrameId: string};
    }
  | {
      stepType: PipelineStepType.JOIN;
      stepInput: {leftDataFrameId: string; rightDataFrameId: string};
    }
  | {
      stepType: PipelineStepType.REGRESSION_INTERPOLATION;
      // The variables data frame is taken from a known isMulti=False step data shape,
      // so there is no ambiguity that would require the variables data frame ID to be specified
      stepInput: {inputDataFrameId: string; modelVariablesDataFrameId?: string};
    }
  | {
      stepType: PipelineStepType.SEND_TO_DOCUMENT;
      stepInput: SendToDocumentStepInput;
    }
  | {
      stepType: PipelineStepType.UNION_DATASETS;
      stepInput: {primaryDataFrameId: string; secondaryDataFrameId: string};
    };

export interface DraftStepInputParents {
  [stepDataName: string]: Array<DataReference>;
}

export type StepInputParents<
  StepType extends PipelineStepType,
  StepVersion extends number,
  InputDataReference = DataReference,
> = Record<keyof GetPipelineStepShape<StepType, StepVersion>['inputDataShapes'], Array<InputDataReference>>;

/**
 * Parameters for addAndRunPipelineStep for a specific step type and version.
 */
export type AddStepParamsForStep<
  StepType extends PipelineStepType,
  StepVersion extends number,
  InputDataReference = DataReference,
  StepConfig = PipelineStepConfig<StepType, StepVersion>,
> = {
  stepType: StepType;
  stepVersion: StepVersion;
  stepInputParents?: StepInputParents<StepType, StepVersion, InputDataReference>;
  stepOutputChildren?: Record<
    keyof GetPipelineStepShape<StepType, StepVersion>['outputDataShapes'],
    Array<DataReference>
  >;
} & StepInputDataFrames<StepType> &
  ([StepConfig] extends [never] ? {stepConfig?: never} : {stepConfig: StepConfig});

/**
 * Parameters for addAndRunPipelineStep for a step type that creates data frames without input, and
 * is not expected to have parent or child steps specified at creation-time.
 */
type AddStepParamsForSourceStep<
  StepType extends PipelineStepType,
  StepVersion extends number,
  InputDataReference = DataReference,
  StepConfig = PipelineStepConfig<StepType, StepVersion>,
> = Pick<
  AddStepParamsForStep<StepType, StepVersion, InputDataReference, StepConfig>,
  'stepType' | 'stepVersion' | 'stepConfig'
>;

export type AddOrReplaceAndRunStepParams<InputDataReference = DataReference> =
  | AddStepParamsForSourceStep<
      PipelineStepType.INITIALIZE_DATASET,
      2,
      InputDataReference,
      PersistedRunQueryData & {
        dataFrameName: string;
        dashboardId: string;
        blockId: string;
      }
    >
  | AddStepParamsForSourceStep<
      PipelineStepType.PARAMETER_OPTIMIZATION_MODELING,
      0,
      InputDataReference,
      {
        dataDefinitionConfig: JSONObject;
        datasetConfiguration: {};
      }
    >
  | AddStepParamsForSourceStep<PipelineStepType.REGISTRY_SCHEMA_DATASET, 0, InputDataReference>
  | AddStepParamsForSourceStep<PipelineStepType.THIRD_PARTY, 0, InputDataReference>
  | AddStepParamsForSourceStep<PipelineStepType.UPLOAD_CSV_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.AGGREGATE_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.ANOVA, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.BIN_DATA, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.CHAI1, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.CHART_VIEW, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.COMPUTED_COLUMN, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.CONNECTION_DATA_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.DATA_CATALOG_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.DATASET_VIEW, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.EXPERIMENT_OPTIMIZATION_BOOTSTRAP, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.EXPERIMENT_OPTIMIZATION_CLEAN_DATA, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.EXPERIMENT_OPTIMIZATION_EXPLANATION, 0, InputDataReference>
  | AddStepParamsForStep<
      PipelineStepType.EXPERIMENT_OPTIMIZATION_GENERATE_SEARCH_SPACE,
      0,
      InputDataReference
    >
  | AddStepParamsForStep<PipelineStepType.EXPERIMENT_OPTIMIZATION_RECOMMENDATION, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.EXPERIMENT_OPTIMIZATION_TRAINING, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.EXPLORATORY_DATA_ANALYSIS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.EXPORT_DATA_CATALOG_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.FILTER_DATASET, 0, InputDataReference, DataFrameFilterStepConfig>
  | AddStepParamsForStep<PipelineStepType.FIND_AND_REPLACE, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.FLAG_ROWS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.HIDE_COLUMNS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.INTERPOLATE_MARKDOWN, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.JOIN, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.LAYOUT, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.LOOK_UP, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.MAP_COLUMNS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.PIVOT_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.PLATE_SCHEMA_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.PROMOTE_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.REGRESSION, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.REGRESSION_INTERPOLATION, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.RENAME_COLUMNS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.REORDER_COLUMNS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.RETYPE_COLUMNS, 0, InputDataReference>
  // TODO: BNCH-104744: remove step version bump temporarily backward compat code
  | AddStepParamsForStep<PipelineStepType.SAVE_TO_RESULTS, 1, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.SAVE_TO_RESULTS, 2, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.SEND_TO_DOCUMENT, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.SORT_COLUMNS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.TABLE_VIEW, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.T_TEST, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.UNION_DATASETS, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.UNPIVOT_DATASET, 0, InputDataReference>
  | AddStepParamsForStep<PipelineStepType.WINDOW_FUNCTIONS, 0, InputDataReference>;

/** All fields can be deep partial because the step will not be run by this endpoint. */
export type AddStepParams<InputDataReference = DataReference> = DeepPartial<
  AddOrReplaceAndRunStepParams<InputDataReference>
>;

/** Use AddStepParams (with its deep partial fields) because if runStep is false, then the new step will not be run. */
export type AddAndRunStepsParams = Array<
  AddStepParams<MakeFieldsOptional<DataReference, 'stepId'>> & {runStep: boolean}
>;

export enum StepReference {
  STEP_ID = 'STEP_ID',
  INDEX = 'INDEX',
}
interface StepReferenceById {
  type: StepReference.STEP_ID;
  stepId: string;
}
interface StepReferenceByIndex {
  type: StepReference.INDEX;
  index: number;
}
export interface ParentStepReference {
  stepReference: StepReferenceById | StepReferenceByIndex;
  stepDataName: string;
  fromStepDataIndex?: number | null;
}
export interface AddStepWithParentStepReference<StepType extends PipelineStepType> {
  step: Extract<AddOrReplaceAndRunStepParams<ParentStepReference>, {stepType: StepType}>;
}
export type AddAndRunStepsParamsV2 = Array<AddStepWithParentStepReference<PipelineStepType>>;

/**
 * We can't use Pick<..., 'stepInputParents'> because AddOrReplaceAndRunStepParams is a union type. We
 * don't use StepInputParents so that we don't have to type the updatePipelineStep and updateAndRunPipelineStep functions.
 * */
export type UpdateAndRunStepParams = {
  stepId: string;
} & DeepPartial<Pick<AddOrReplaceAndRunStepParams, 'stepConfig'>> & {
    stepInputParents?: DraftStepInputParents;
  };

export interface ReplaceOptions {
  originalStepId: string;
  columnMapping?: Record<string, string>;
  allDisplayNames?: Array<string>;
}
export interface AddOrReplaceStepResponse {
  stepId: string;
}
export interface AddStepsResponse {
  stepIds: Array<string>;
}

export interface AddAndUpdateStepsResponse {
  pipelineId: string;
  newStepIds: Array<string>;
}

export function addAndRunPipelineStep(
  pipelineId: string,
  data: AddOrReplaceAndRunStepParams
): Promise<AddOrReplaceStepResponse> {
  return net.postJSONPromise({
    url: `/1/api/pipelines/${pipelineId}:add-and-run-step`,
    data: {step: data},
  });
}

export function addAndRunPipelineSteps(
  pipelineId: string,
  data: AddAndRunStepsParams
): Promise<AddStepsResponse> {
  return net.postJSONPromise({
    url: `/1/api/pipelines/${pipelineId}:add-and-run-steps`,
    data: {steps: data},
  });
}

export function addAndRunPipelineStepsV2(
  pipelineId: string,
  data: AddAndRunStepsParamsV2
): Promise<AddStepsResponse> {
  return net.postJSONPromise({
    url: `/1/api/pipelines/${pipelineId}:add-and-run-steps-v2`,
    data: {steps: data},
  });
}

export type PipelineEdgeUpsert = ({fromStepId: string} | {fromStepIndex: number}) & {
  fromStepDataName: string;
  fromStepDataIndex: number | null;
  toStepDataName: string;
  toStepDataIndex: number | null;
};

export type PipelineEdgeUpsertById = Extract<PipelineEdgeUpsert, {fromStepId: string}>;
export type PipelineEdgeUpsertByIndex = Extract<PipelineEdgeUpsert, {fromStepIndex: number}>;
export interface CreateStepParams {
  stepType: PipelineStepType;
  stepVersion: number;
  config?: Record<string, JSONValue>;
  incomingEdges?: Array<PipelineEdgeUpsert>;
  runAfterCreate?: boolean;
}
export interface UpdateStepParams {
  stepId: string;
  config?: Record<string, JSONValue>;
  incomingEdges?: Array<PipelineEdgeUpsert>;
  rerunAfterUpdate?: boolean;
}
export interface AddAndUpdateStepParams {
  stepsToAdd: Array<CreateStepParams>;
  stepsToUpdate: Array<UpdateStepParams>;
}

export function addAndUpdatePipelineSteps(
  pipelineId: string,
  data: AddAndUpdateStepParams
): Promise<AddAndUpdateStepsResponse> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:add-and-update-steps`, data});
}

export function copyPipelineSteps(
  pipelineId: string,
  data: {stepIds: Array<string>}
): Promise<{stepIds: Array<string>}> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:copy-steps`, data});
}

/**
 * @deprecated This endpoint uses legacy "view" logic. Use `copyPipelineSteps`.
 */
export function copyPipelineView(
  pipelineId: string,
  data: {viewStepId: string}
): Promise<{stepIds: Array<string>}> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:copy-view`, data});
}

/**
 * Keep in sync with `benchling/pipelines/steps/delete_pipeline_steps_and_update_lineage.py`
 */
export enum DeletePipelineStepsBehavior {
  DELETE_ALL_CHILDREN = 'DELETE_ALL_CHILDREN',
  DELETE_ALL_CHILD_TRANSFORMATION_STEPS = 'DELETE_ALL_CHILD_TRANSFORMATION_STEPS',
  NO_INFERENCE = 'NO_INFERENCE',
  NO_INFERENCE_WITH_REPARENTING = 'NO_INFERENCE_WITH_REPARENTING',
}

export function deletePipelineSteps(
  pipelineId: string,
  data: {stepIds: Array<string>; behavior: DeletePipelineStepsBehavior}
): Promise<{}> {
  return net.deletePromise({url: `/1/api/pipelines/${pipelineId}:delete-analysis-steps`, data});
}

export function replaceAndRunPipelineStep(
  pipelineId: string,
  data: AddOrReplaceAndRunStepParams,
  replaceOptions: ReplaceOptions
): Promise<AddOrReplaceStepResponse> {
  const {originalStepId, columnMapping, allDisplayNames} = replaceOptions;
  return net.postJSONPromise({
    url: `/1/api/pipelines/${pipelineId}:replace-and-run-step`,
    data: {step: data, originalStepId, columnMapping, allDisplayNames},
  });
}

export function updatePipelineStep(pipelineId: string, data: UpdateAndRunStepParams): Promise<void> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:update-step`, data});
}

export function updateAndRunPipelineStep(pipelineId: string, data: UpdateAndRunStepParams): Promise<void> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:update-and-run-step`, data});
}

export function runPipelineStep(pipelineId: string, data: {stepId: string}): Promise<void> {
  return net.postJSONPromise({url: `/1/api/pipelines/${pipelineId}:run-step`, data});
}

export interface PreviewStepParams<StepType extends PipelineStepType, StepVersion extends number = 0> {
  stepType: PipelineStepType;
  stepConfig: PipelineStepConfig<StepType, StepVersion>;
  stepInput?: {[stepDataName: string]: string | Array<string>};
}

export type PreviewChart = Pick<Chart, 'library' | 'libraryConfig' | 'libraryVersion'>;
export type PreviewScratchFile = Pick<
  GetAnalysisScratchFile,
  'id' | 'filename' | 'downloadUrl' | 'name' | 'externalFileId' | 'mimeType'
>;

export type PreviewRawData = DataFrame | PreviewChart | PreviewScratchFile;

function getPreviewRawDataKind(previewRawData: PreviewRawData): PipelineStepDataKind {
  if (previewRawData.hasOwnProperty('libraryConfig') && previewRawData.hasOwnProperty('library')) {
    return PipelineStepDataKind.CHART;
  } else if (previewRawData.hasOwnProperty('columns') && previewRawData.hasOwnProperty('rows')) {
    return PipelineStepDataKind.DATASET;
  } else if (previewRawData.hasOwnProperty('filename') && previewRawData.hasOwnProperty('downloadUrl')) {
    return PipelineStepDataKind.FILE;
  } else {
    throw new Error('Unexpected step data type');
  }
}

export function isPreviewDataFrame(rawData: PreviewRawData): rawData is DataFrame {
  return getPreviewRawDataKind(rawData) === PipelineStepDataKind.DATASET;
}

export function isPreviewChart(rawData: PreviewRawData): rawData is PreviewChart {
  return getPreviewRawDataKind(rawData) === PipelineStepDataKind.CHART;
}

export function isPreviewFile(rawData: PreviewRawData): rawData is PreviewScratchFile {
  return getPreviewRawDataKind(rawData) === PipelineStepDataKind.FILE;
}

interface PreviewRawDataMap {
  [PipelineStepDataKind.DATASET]: DataFrame;
  [PipelineStepDataKind.CHART]: PreviewChart;
  [PipelineStepDataKind.FILE]: PreviewScratchFile;
}

type PreviewOutputRecord<StepType extends PipelineStepType, StepVersion extends number> = {
  [Key in keyof OutputStepDatasRecord<StepType, StepVersion>]: Array<PreviewRawData>;
};

export interface PreviewOutput<
  StepType extends PipelineStepType = PipelineStepType,
  StepVersion extends number = number,
> {
  outputStepDatas: PreviewOutputRecord<StepType, StepVersion>;
}

/**
 * Extract output step data by name from a PreviewOutput.
 *
 * Specify kind to assert and narrow the preview step data type
 */
export function getPreviewOutputStepData<
  StepType extends PipelineStepType,
  StepVersion extends number,
  StepDataName extends keyof PreviewOutputRecord<StepType, StepVersion>,
  Kind extends PipelineStepDataKind = PipelineStepDataKind,
>(
  previewOutput: PreviewOutput<StepType, StepVersion>,
  stepDataName: StepDataName,
  kind?: Kind
): Array<PreviewRawDataMap[Kind]> {
  const outputStepData = previewOutput.outputStepDatas[stepDataName];
  assert(
    kind == null || outputStepData.every((previewRawData) => getPreviewRawDataKind(previewRawData) === kind),
    'Got unexpected preview data kinds',
    {
      expectedKind: kind,
      previewRawDataKinds: outputStepData.map((previewRawData) => getPreviewRawDataKind(previewRawData)),
    }
  );

  return outputStepData as Array<PreviewRawDataMap[Kind]>;
}

export function previewStep<StepType extends PipelineStepType, StepVersion extends number = 0>(
  data: PreviewStepParams<StepType, StepVersion>
): Promise<PreviewOutput<StepType, StepVersion>> {
  return TaskRunner.postJSONPromise({url: '/1/api/pipelines:preview-step', data: {step: data}});
}

export function archiveSavedResultsFromPipelineStep(stepId: string, purpose: string): Promise<void> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/pipelines:archive-saved-results-from-step',
    data: {stepId, purpose},
  });
}

export function unarchiveSavedResultsFromPipelineStep(stepId: string): Promise<void> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/pipelines:unarchive-saved-results-from-step',
    data: {stepId},
  });
}

export function exportSavedResultsDataFrame(stepId: string): Promise<{url: string}> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/pipelines:export-saved-results-data-frame`,
    data: {stepId},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchivePipelines(
  pipelineIds: Array<string>,
  dataModsHeader?: DataModificationsHeader
): Promise<BulkUnarchiveResponse> {
  return net.postJSONPromise({
    url: '/1/api/pipelines:bulk-unarchive',
    data: {ids: pipelineIds},
    extraHeaders: dataModsHeader,
  });
}

export function bulkArchivePipelines(
  pipelineIds: Array<string>,
  purpose: string,
  resultPurpose: string | null,
  dataModsHeader?: DataModificationsHeader
): Promise<BulkArchiveItemsResponse> {
  return net.postJSONPromise({
    url: '/1/api/pipelines:bulk-archive',
    data: {ids: pipelineIds, purpose, resultPurpose},
    extraHeaders: dataModsHeader,
  });
}

export interface ApplyStaticTemplateParams {
  staticTemplateId: string;
  variableValues: Record<string, unknown>;
  inputReferences: Array<{
    fromStepId: string;
    fromStepDataName: string;
    fromStepDataIndex: number;
  }>;
}

export interface ApplyStaticTemplateResponse {
  newStepIds: Array<string>;
}

export function applyStaticTemplate(
  pipelineId: string,
  data: ApplyStaticTemplateParams
): Promise<ApplyStaticTemplateResponse> {
  return net.postJSONPromise({
    url: `/1/api/pipelines/${pipelineId}:apply-static-template`,
    data,
  });
}

export interface GenerateStepConfigResponse {
  stepShape: GetPipelineConfigSpecType<'shape'>;
  config: Record<string, JSONValue>;
}

export function generateStepConfig(
  stepShape: GetPipelineConfigSpecType<'shape'>,
  config: Record<string, JSONValue>,
  stepType: string,
  stepId: string | undefined
): Promise<GenerateStepConfigResponse> {
  return net.postJSONPromise({
    url: '/1/api/pipelines:generate-step-config',
    data: {stepShape, config, stepType, stepId},
  });
}
